import { motion } from 'framer-motion';
import { Shield, FileText, Users, Scale, CreditCard, AlertTriangle, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function TermsPage() {
  const currentDate = new Date().toLocaleDateString('fr-FR', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-black-rich pt-24 pb-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gold-500/10 mb-6">
            <FileText className="w-8 h-8 text-gold-500" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
            Conditions Générales d'Utilisation et de Vente
          </h1>
          <p className="text-lg text-zinc-400">
            Version 1.0 - En vigueur au {currentDate}
          </p>
        </motion.div>

        {/* Table of Contents */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6 mb-8"
        >
          <h2 className="text-lg font-heading font-bold text-white mb-4">Sommaire</h2>
          <ul className="space-y-2 text-zinc-400">
            <li><a href="#article-1" className="hover:text-gold-500 transition-colors">Article 1 - Objet</a></li>
            <li><a href="#article-2" className="hover:text-gold-500 transition-colors">Article 2 - Description du Service</a></li>
            <li><a href="#article-3" className="hover:text-gold-500 transition-colors">Article 3 - Accès au Service</a></li>
            <li><a href="#article-4" className="hover:text-gold-500 transition-colors">Article 4 - Inscription et Compte Utilisateur</a></li>
            <li><a href="#article-5" className="hover:text-gold-500 transition-colors">Article 5 - Tarifs et Paiement</a></li>
            <li><a href="#article-6" className="hover:text-gold-500 transition-colors">Article 6 - Limitations du Service</a></li>
            <li><a href="#article-7" className="hover:text-gold-500 transition-colors">Article 7 - Responsabilité</a></li>
            <li><a href="#article-8" className="hover:text-gold-500 transition-colors">Article 8 - Propriété Intellectuelle</a></li>
            <li><a href="#article-9" className="hover:text-gold-500 transition-colors">Article 9 - Données Personnelles</a></li>
            <li><a href="#article-10" className="hover:text-gold-500 transition-colors">Article 10 - Résiliation</a></li>
            <li><a href="#article-11" className="hover:text-gold-500 transition-colors">Article 11 - Droit Applicable</a></li>
          </ul>
        </motion.div>

        {/* Articles */}
        <div className="space-y-8">
          {/* Article 1 */}
          <motion.section
            id="article-1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="glass-card p-6"
          >
            <h2 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center text-gold-500 text-sm font-bold">1</span>
              Objet
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Les présentes Conditions Générales d'Utilisation et de Vente (ci-après "CGU/CGV") régissent l'utilisation du service Assistant by Léon, accessible via le site web et l'application mobile. En utilisant le Service, l'Utilisateur accepte sans réserve les présentes CGU/CGV.
            </p>
          </motion.section>

          {/* Article 2 */}
          <motion.section
            id="article-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-6"
          >
            <h2 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center text-gold-500 text-sm font-bold">2</span>
              Description du Service
            </h2>
            <div className="text-zinc-400 leading-relaxed space-y-4">
              <p>
                Assistant by Léon propose un service numérique d'assistance à l'analyse de devis et documents travaux reposant notamment sur des traitements automatisés par intelligence artificielle.
              </p>
              <p>Le Service comprend notamment :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>L'analyse automatisée de devis travaux</li>
                <li>La comparaison de devis</li>
                <li>L'estimation de budget travaux</li>
                <li>La génération de guides travaux</li>
                <li>Un assistant IA spécialisé bâtiment</li>
              </ul>
            </div>
          </motion.section>

          {/* Article 3 */}
          <motion.section
            id="article-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="glass-card p-6"
          >
            <h2 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center text-gold-500 text-sm font-bold">3</span>
              Accès au Service
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              L'accès au Service nécessite une connexion Internet et un navigateur compatible. L'Utilisateur est responsable de son équipement informatique et de sa connexion. Assistant by Léon ne garantit pas la disponibilité permanente du Service et se réserve le droit de suspendre temporairement l'accès pour maintenance.
            </p>
          </motion.section>

          {/* Article 4 */}
          <motion.section
            id="article-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-6"
          >
            <h2 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center text-gold-500 text-sm font-bold">4</span>
              Inscription et Compte Utilisateur
            </h2>
            <div className="text-zinc-400 leading-relaxed space-y-4">
              <p>
                L'utilisation du Service nécessite la création d'un compte utilisateur. L'Utilisateur s'engage à fournir des informations exactes et à jour, et à maintenir la confidentialité de ses identifiants de connexion.
              </p>
              <p>
                En créant un compte, l'Utilisateur déclare avoir lu et accepté les présentes CGU/CGV. Cette acceptation est enregistrée avec horodatage.
              </p>
            </div>
          </motion.section>

          {/* Article 5 */}
          <motion.section
            id="article-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="glass-card p-6"
          >
            <h2 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center text-gold-500 text-sm font-bold">5</span>
              Tarifs et Paiement
            </h2>
            <div className="text-zinc-400 leading-relaxed space-y-4">
              <p>
                Le Service est proposé selon deux formules :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-white">Plan Gratuit :</strong> Accès limité aux fonctionnalités de base</li>
                <li><strong className="text-white">Plan Premium :</strong> 19€/mois, accès illimité à toutes les fonctionnalités</li>
              </ul>
              <p>
                Les paiements sont gérés via Stripe. L'abonnement Premium est renouvelé automatiquement chaque mois, sauf résiliation par l'Utilisateur.
              </p>
            </div>
          </motion.section>

          {/* Article 6 - Important */}
          <motion.section
            id="article-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-card p-6 border-gold-500/30"
          >
            <h2 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center text-gold-500 text-sm font-bold">6</span>
              Limitations du Service
            </h2>
            <div className="text-zinc-400 leading-relaxed space-y-4">
              <div className="p-4 bg-gold-500/5 border border-gold-500/20 rounded-xl">
                <p className="text-zinc-300 font-medium mb-2">Important :</p>
                <p>
                  Les résultats, scores, alertes, recommandations, emails générés et rapports PDF sont fournis à titre strictement indicatif pour aider l'Utilisateur à apprécier certains points d'attention.
                </p>
              </div>
              <p>
                Le Service ne remplace pas une visite sur site, un relevé, une étude technique, une mission de maîtrise d'œuvre, un audit contradictoire, un contrôle de conformité, un conseil juridique, ni l'intervention d'un artisan, technicien, bureau d'études, expert ou assureur.
              </p>
              <p>
                L'Utilisateur reconnaît que les analyses peuvent être partielles, dépendre de la qualité des documents transmis, comporter des approximations et ne pas révéler l'ensemble des risques techniques, administratifs, financiers ou contractuels.
              </p>
            </div>
          </motion.section>

          {/* Article 7 - Important */}
          <motion.section
            id="article-7"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="glass-card p-6 border-orange-500/30"
          >
            <h2 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-500 text-sm font-bold">7</span>
              Responsabilité
            </h2>
            <div className="text-zinc-400 leading-relaxed space-y-4">
              <p>
                Assistant by Léon est tenu d'une obligation de moyens dans la fourniture du Service numérique. L'Utilisateur demeure seul responsable de ses choix, signatures, arbitrages, paiements, validations et décisions relatives au chantier.
              </p>
              <p>
                Avant toute signature ou exécution de travaux, l'Utilisateur doit faire procéder, si nécessaire, à toute vérification complémentaire utile auprès de professionnels qualifiés et assurés.
              </p>
              <div className="p-4 bg-orange-500/5 border border-orange-500/20 rounded-xl">
                <p className="text-orange-400 font-medium mb-2">Exclusion de responsabilité :</p>
                <p>
                  Sauf faute prouvée dans l'exécution du Service numérique lui-même, Assistant by Léon ne répond pas des malfaçons, désordres, abandons de chantier, non-conformités, surcoûts, retards, omissions, sinistres, défauts d'assurance, erreurs de devis, ni des conséquences de l'exécution des travaux par des tiers.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Article 8 */}
          <motion.section
            id="article-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-card p-6"
          >
            <h2 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center text-gold-500 text-sm font-bold">8</span>
              Propriété Intellectuelle
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              L'ensemble des éléments du Service (textes, graphismes, logiciels, marques, etc.) sont protégés par le droit de la propriété intellectuelle. Toute reproduction ou représentation, totale ou partielle, est interdite sans autorisation préalable.
            </p>
          </motion.section>

          {/* Article 9 */}
          <motion.section
            id="article-9"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="glass-card p-6"
          >
            <h2 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center text-gold-500 text-sm font-bold">9</span>
              Données Personnelles
            </h2>
            <div className="text-zinc-400 leading-relaxed space-y-4">
              <p>
                Assistant by Léon collecte et traite les données personnelles de l'Utilisateur conformément au Règlement Général sur la Protection des Données (RGPD). Les données sont utilisées pour la fourniture du Service et ne sont pas cédées à des tiers sans consentement.
              </p>
              <p>
                L'Utilisateur dispose d'un droit d'accès, de rectification, d'effacement et de portabilité de ses données. Pour exercer ces droits, contacter : contact@phenix-inspect.fr
              </p>
            </div>
          </motion.section>

          {/* Article 10 */}
          <motion.section
            id="article-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="glass-card p-6"
          >
            <h2 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center text-gold-500 text-sm font-bold">10</span>
              Résiliation
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              L'Utilisateur peut résilier son compte à tout moment depuis son espace personnel ou en contactant le support. L'abonnement Premium peut être résilié avant chaque date de renouvellement. En cas de violation des CGU/CGV, Assistant by Léon se réserve le droit de suspendre ou supprimer le compte de l'Utilisateur.
            </p>
          </motion.section>

          {/* Article 11 */}
          <motion.section
            id="article-11"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            className="glass-card p-6"
          >
            <h2 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center text-gold-500 text-sm font-bold">11</span>
              Droit Applicable
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Les présentes CGU/CGV sont régies par le droit français. En cas de litige, les parties s'engagent à rechercher une solution amiable. À défaut, les tribunaux français seront seuls compétents.
            </p>
          </motion.section>
        </div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-12 text-center space-y-4"
        >
          <div className="flex items-center justify-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <p className="text-zinc-400">
              Dernière mise à jour : {currentDate}
            </p>
          </div>
          <p className="text-zinc-500 text-sm">
            Pour toute question, contactez-nous à <a href="mailto:contact@phenix-inspect.fr" className="text-gold-500 hover:underline">contact@phenix-inspect.fr</a>
          </p>
          <Link 
            to="/legal" 
            className="inline-block text-gold-500 hover:text-gold-400 transition-colors"
          >
            Voir aussi : Mentions et limites du service →
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
